import { memo, useMemo } from 'react';

import { LocationsFilterLibraryGeneralTreeItem } from '~/components/Filters/LocationsFilter/LocationsFilterLibraryGeneralTreeItem';
import {
  LocationsFilterLibraryTreeItem,
  type LocationsFilterLibraryTreeItemProps,
} from '~/components/Filters/LocationsFilter/LocationsFilterLibraryTreeItem';
import { LocationsFilterLoadingSkeleton } from '~/components/Filters/LocationsFilter/LocationsFilterLoadingSkeleton';
import { useLocationsFilterData } from '~/components/Filters/LocationsFilter/useLocationsFilterData';

export type LocationsFilterLibraryTreeProps = Pick<
  LocationsFilterLibraryTreeItemProps,
  'onLibrarySelect' | 'onBoardSelect'
> & {
  search: string;
};

export const LocationsFilterLibraryTree = memo(
  ({ onLibrarySelect, onBoardSelect, search }: LocationsFilterLibraryTreeProps) => {
    const { libraries, boards: boardsMap, isLoading } = useLocationsFilterData({ search });
    const boards = useMemo(() => Array.from(boardsMap.values()), [boardsMap]);

    if (search && isLoading) return <LocationsFilterLoadingSkeleton />;

    const hasSearchResults = boards.length > 0 || libraries.length > 0;

    if (search && !hasSearchResults) {
      return <p className="px-2 py-1 text-12 text-grey-10">No results</p>;
    }

    return (
      <div data-testid="LOCATIONS_FILTER_LIBRARY_TREE">
        {(!search || boards.length > 0) && (
          <LocationsFilterLibraryGeneralTreeItem boards={search ? boards : undefined} onBoardSelect={onBoardSelect} />
        )}
        {libraries.map((library) => (
          <LocationsFilterLibraryTreeItem
            key={library.id}
            library={library}
            onBoardSelect={onBoardSelect}
            onLibrarySelect={onLibrarySelect}
          />
        ))}
      </div>
    );
  },
);

LocationsFilterLibraryTree.displayName = 'LocationsFilterLibraryTree';
